import { createRoute, getRouteApi, lazyRouteComponent } from '@tanstack/react-router';
import { z } from 'zod/v3';

import { CountryCode } from 'common/api-types/common';
import { menuLayoutRoute } from 'root/MenuLayout';

const pricingSearchParamsSchema = z.object({
  from: z.string().optional(),
  activeTab: z.enum(['RECOMMENDATIONS', 'PRICES', 'NOT_APPLIED']).optional(),
  isKviOverridesOpen: z.boolean().optional(),
  isCategorySegmentOverridesOpen: z.boolean().optional(),
  isSimilarityMappingsUploadOpen: z.boolean().optional(),
  isDownloadCatalogProductsModalOpen: z.boolean().optional(),
  isPriceIndexOverridesOpen: z.boolean().optional(),
  isExperimentalKviTypesUploadOpen: z.boolean().optional(),
  productsMissingScalingDataIds: z.array(z.string()).optional(),
  q: z.coerce.string().optional(),
  groupByProduct: z.boolean().optional(),
  sortBy: z
    .enum([
      'NEWEST_FIRST',
      'OLDEST_FIRST',
      'PROFIT_IMPACT_DESC',
      'PROFIT_IMPACT_ASC',
      'PPM_CHANGE_DESC',
      'PPM_CHANGE_ASC',
      'PRICE_CHANGE_PERCENTAGE_DESC',
      'PRICE_CHANGE_PERCENTAGE_ASC',
      'LAST_APPROVED_DATE_DESC',
      'LAST_APPROVED_DATE_ASC',
    ])
    .optional(),
  venueIds: z.array(z.string()).optional(),
  excludedVenueIds: z.array(z.string()).optional(),
  regionIds: z.array(z.string()).optional(),
  vendorIds: z.array(z.string()).optional(),
  actionSentByIds: z.array(z.string()).optional(),
  productFamilyIds: z.array(z.string()).optional(),
  kviTypes: z.array(z.enum(['KVI', 'NON_KVI', 'SUPER_KVI'])).optional(),
  sources: z
    .array(
      z.enum([
        'COMPETITORS_BENCHMARK',
        'BASE_PRICING',
        'PRICE_FLOOR',
        'PRICE_CAP',
        'PRICE_CONSISTENCY_GROUP',
        'MANUAL_OVERRIDE',
        'OTHER',
        'ML_BASE_PRICING',
        'EXPERIMENTATION',
      ]),
    )
    .optional(),
  brandIds: z.array(z.string()).optional(),
  categorySegments: z
    .array(z.enum(['STARS', 'TRAFFIC', 'GOLDEN_TAIL', 'ZOMBIES', 'NO_SALES']))
    .optional(),
  revertPriceModalOpen: z.boolean().optional(),
  priceRowId: z.string().optional(),
  experimentPricesCount: z.coerce.number().optional(),
  historicalPriceSuggestionStatuses: z
    .array(z.enum(['APPROVED', 'REJECTED', 'PENDING', 'SUPERSEDED', 'FAILED_TO_APPLY']))
    .optional(),
  minAbsolutePriceChange: z.string().optional(),
  maxAbsolutePriceChange: z.string().optional(),
  minRelativePriceChange: z.string().optional(),
  maxRelativePriceChange: z.string().optional(),
  lastApprovedStartDate: z.string().optional(),
  lastApprovedEndDate: z.string().optional(),
  inconsistentSuggestionsToken: z.string().optional(),
  inconsistentSuggestionsNames: z.array(z.string()).optional(),
  selectedFilter: z
    .enum([
      'venues',
      'vendors',
      'brands',
      'productFamilies',
      'kviType',
      'source',
      'categorySegment',
      'actionBy',
      'priceRange',
      'status',
      'lastApprovedDate',
    ])
    .optional(),
  priceTypes: z.array(z.enum(['STANDARD', 'MEMBERSHIP', 'DEAL', 'LOYALTY'])).optional(),
  v1: z.boolean().optional(),
});

const pricingMainRoute = createRoute({
  getParentRoute: () => menuLayoutRoute,
  path: 'pricing',
});

export type PricingSearchRouteSchemaType = z.infer<typeof pricingSearchParamsSchema>;

export const pricingViewRoute = createRoute({
  getParentRoute: () => pricingMainRoute,
  path: '/',
  component: lazyRouteComponent(() => import('./PricingView/PricingView')),
  validateSearch: pricingSearchParamsSchema.parse,
});

export const pricingMainRouteApi = getRouteApi('/auth/merchant/menuLayout/pricing');
export const pricingRouteApi = getRouteApi('/auth/merchant/menuLayout/pricing/');
export const pricingProductRouteApi = getRouteApi(
  '/auth/merchant/menuLayout/pricing/product/$countryCode/$productId',
);

export const pricingProductViewRoute = createRoute({
  getParentRoute: () => pricingMainRoute,
  path: 'product/$countryCode/$productId',
  component: lazyRouteComponent(() => import('./ProductPricingView/ProductPricingView')),
  validateSearch: pricingSearchParamsSchema.parse,
  parseParams: (params) => ({
    countryCode: z.string().parse(params.countryCode) as CountryCode,
    productId: z.string().parse(params.productId),
  }),
});

export const priceSummarySectionRoute = createRoute({
  getParentRoute: () => pricingProductViewRoute,
  path: '/',
  component: lazyRouteComponent(
    () => import('./ProductPricingView/PriceSummarySection/PriceSummarySection'),
  ),
});

export const priceHistoryRoute = createRoute({
  getParentRoute: () => pricingProductViewRoute,
  path: 'history',
  component: lazyRouteComponent(
    () => import('./ProductPricingView/PriceHistorySection/PriceHistorySection'),
  ),
});

export const basePricingSectionRoute = createRoute({
  getParentRoute: () => pricingProductViewRoute,
  path: 'base-pricing',
  component: lazyRouteComponent(
    () => import('./ProductPricingView/BasePricingSection/BasePricingSection'),
  ),
});

export const overrideSectionRoute = createRoute({
  getParentRoute: () => pricingProductViewRoute,
  path: 'overrides',
  component: lazyRouteComponent(
    () => import('./ProductPricingView/OverridesSection/OverridesSection'),
  ),
});

export const pricingRouteTree = pricingMainRoute.addChildren({
  pricingViewRoute,
  pricingProductViewRoute: pricingProductViewRoute.addChildren({
    priceSummarySectionRoute,
    priceHistoryRoute,
    basePricingSectionRoute,
    overrideSectionRoute,
  }),
});
